import type {
  PostWebauthnRegisterOptions,
  PostWebauthnRegisterVerify,
  PostWebauthnAuthenticateOptions,
  PostWebauthnAuthenticateVerify,
  GetWebauthnCredentials,
  DeleteWebauthnCredential,
} from "@/server-types";
import { del, get, post, withStepUpToken } from "../request-helpers";
import { useCustomMutation } from "../custom-mutation";
import { useCustomQuery } from "../custom-query";

/**
 * GET /user/webauthn/credentials
 */
export const useWebauthnCredentials = (enabled = true) => {
  return useCustomQuery({
    queryKey: ["webauthn-credentials"],
    queryFn: () => get<GetWebauthnCredentials>("/user/webauthn/credentials"),
    enabled,
  });
};

/**
 * POST /user/webauthn/register/options
 */
export const useWebauthnRegisterOptions = () => {
  return useCustomMutation({
    mutationKey: ["webauthn-register-options"],
    mutationFn: () =>
      post<PostWebauthnRegisterOptions>("/user/webauthn/register/options"),
  });
};

/**
 * POST /user/webauthn/register/verify
 */
export const useWebauthnRegisterVerify = () => {
  return useCustomMutation({
    mutationKey: ["webauthn-register-verify"],
    mutationFn: ({
      stepUpToken,
      ...body
    }: PostWebauthnRegisterVerify["body"] & { stepUpToken: string }) =>
      post<PostWebauthnRegisterVerify>(
        "/user/webauthn/register/verify",
        body,
        undefined,
        withStepUpToken(stepUpToken),
      ),
  });
};

/**
 * POST /user/webauthn/authenticate/options
 */
export const useWebauthnAuthenticateOptions = () => {
  return useCustomMutation({
    mutationKey: ["webauthn-authenticate-options"],
    mutationFn: (body: PostWebauthnAuthenticateOptions["body"]) =>
      post<PostWebauthnAuthenticateOptions>(
        "/user/webauthn/authenticate/options",
        body,
      ),
  });
};

/**
 * POST /user/webauthn/authenticate/verify
 */
export const useWebauthnAuthenticateVerify = () => {
  return useCustomMutation({
    mutationKey: ["webauthn-authenticate-verify"],
    mutationFn: (body: PostWebauthnAuthenticateVerify["body"]) =>
      post<PostWebauthnAuthenticateVerify>(
        "/user/webauthn/authenticate/verify",
        body,
      ),
  });
};

/**
 * DELETE /user/webauthn/credentials/:credentialId
 */
export const useWebauthnDeleteCredential = () => {
  return useCustomMutation({
    mutationKey: ["webauthn-delete-credential"],
    mutationFn: ({
      credentialId,
      stepUpToken,
    }: {
      credentialId: number;
      stepUpToken: string;
    }) =>
      del<DeleteWebauthnCredential>(
        `/user/webauthn/credentials/${credentialId}`,
        undefined,
        undefined,
        withStepUpToken(stepUpToken),
      ),
  });
};
